/**
 * 전역 키 가드.
 *
 * CAD 편집기는 iframe 안에서 브라우저 기본 단축키와 정면으로 충돌한다.
 *   Ctrl+S  → 브라우저 "페이지 저장"
 *   Ctrl+O  → 파일 열기
 *   Ctrl+P  → 인쇄
 *   F3      → 브라우저 찾기 (AutoCAD 에서는 객체스냅 토글)
 *   Space   → 스크롤 (CAD 에서는 화면 이동 / 명령 확정)
 *
 * 여기서는 "예약키를 선점"만 한다. 실제 동작은 P2 명령 엔진이 구독한다.
 * 입력 필드 안에서는 절대 가로채지 않는다. 대화상자(OneUI)가 떠 있을 때도 비켜선다.
 */

import { OneUI } from '../shell/oneUi';

const RESERVED_CTRL = new Set(['s', 'o', 'p', 'd', 'g']);
const RESERVED_KEYS = new Set(['F1', 'F3', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12']);

function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable ||
    el.getAttribute('role') === 'textbox'
  );
}

export type KeyHandler = (e: KeyboardEvent) => boolean | void;

const handlers = new Set<KeyHandler>();

/** P2 명령 엔진이 키 입력을 구독한다. true 를 반환하면 소비된 것으로 본다. */
export function onReservedKey(fn: KeyHandler): () => void {
  handlers.add(fn);
  return () => handlers.delete(fn);
}

export function installGlobalKeyGuards(): void {
  window.addEventListener(
    'keydown',
    (e) => {
      if (isTextEntry(e.target)) return;

      const ctrl = e.ctrlKey || e.metaKey;
      const reserved =
        (ctrl && RESERVED_CTRL.has(e.key.toLowerCase())) ||
        RESERVED_KEYS.has(e.key) ||
        (e.key === ' ' && !ctrl);

      if (!reserved) return;

      // 대화상자·모달이 떠 있으면 Space 는 그 안의 버튼 몫이다(가로채면 버튼이 안 눌린다).
      // 나머지 예약키는 브라우저 기본 동작만 막고 편집기 명령에는 넘기지 않는다.
      const modalOpen = OneUI.hasOpen();
      if (modalOpen && e.key === ' ') return;

      // 브라우저 / ONE 쉘 기본 동작 차단
      e.preventDefault();
      e.stopPropagation();
      if (modalOpen) return;

      for (const h of handlers) {
        if (h(e) === true) break;
      }
    },
    { capture: true }
  );

  // iframe 안에서 편집기 진입 시 키 입력이 먹지 않는 문제 대응:
  // 문서에 포커스가 없으면 강제로 잡아준다.
  window.addEventListener('pointerdown', () => {
    if (!document.hasFocus()) window.focus();
  });
}
